import logger from '@eventbrite/client-logger';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import Cookies from 'cookies-js';
import {
    ASK_REQUIRED_NON_WAIVER_QUESTIONS_AFTER_PAYMENT,
    ENABLE_CAPTCHA_V3_ON_CHECKOUT,
    ENABLE_CHECKOUT_EXPRESS_PAYPAL_FF,
    GROUP_WAIVER_QUESTIONS,
} from '../constants/splitio';
import { WithSplitioNamespace } from '../state/splitio';

const getOverridenFeatureFlags = () => {
    if (!HAS_WINDOW) {
        return {};
    }

    try {
        const signedFeatureFlagsOverride = Cookies.get('feature_flags_signed');

        const matches = /^data=([^&]+)&sig=/.exec(signedFeatureFlagsOverride);
        const base64FeatureFlagsOverride = matches?.[1];
        if (!base64FeatureFlagsOverride) {
            return {};
        }

        const featureFlagsOverrideString = atob(base64FeatureFlagsOverride);
        const featureFlagsOverride = JSON.parse(featureFlagsOverrideString);

        return Object.fromEntries(
            Object.entries(featureFlagsOverride).map(([name, state]) => [
                name,
                {
                    'overriden-treatment': {
                        treatment:
                            // We cannot import FeatureFlagState directly from @eventbrite/cypress-featureflags because
                            // it breaks TypeScript typechecker
                            state === 1 /* FeatureFlagState.DISABLED */
                                ? 'off'
                                : 'on',
                    },
                },
            ]),
        );
    } catch (error) {
        logger.warn(`Could not get Feature Flags override`, { error });
        return {};
    }
};

export const getSplitTreatmentValue = (
    state: WithSplitioNamespace,
    splitName: string,
): string => {
    const baseFeatureFlags = state.splitio?.treatments ?? {};
    const featureFlagsOverride = getOverridenFeatureFlags();
    const treatments = {
        ...baseFeatureFlags,
        ...featureFlagsOverride,
    };

    return (
        (treatments &&
            treatments[splitName] &&
            Object.values(treatments[splitName])?.[0]?.treatment) ??
        'off'
    );
};

export const isSplitTreatmentEnabled = (
    state: WithSplitioNamespace,
    splitName: string,
) => getSplitTreatmentValue(state, splitName) === 'on';

export const isExpressPayPalFFEnabled = (state: WithSplitioNamespace) =>
    isSplitTreatmentEnabled(state, ENABLE_CHECKOUT_EXPRESS_PAYPAL_FF);

export const getIsGroupWaiverQuestionsEnabled = (state: WithSplitioNamespace) =>
    isSplitTreatmentEnabled(state, GROUP_WAIVER_QUESTIONS);

export const getIsAskRequiredNonWaiverQuestionsAfterPaymentEnabled = (
    state: WithSplitioNamespace,
) =>
    isSplitTreatmentEnabled(
        state,
        ASK_REQUIRED_NON_WAIVER_QUESTIONS_AFTER_PAYMENT,
    );

export const isRecaptchaV3Enabled = (state: WithSplitioNamespace) => {
    return isSplitTreatmentEnabled(state, ENABLE_CAPTCHA_V3_ON_CHECKOUT);
};
